import Vue from 'vue';
import Component from 'vue-class-component';

import { sendItemToAnalytics } from 'common/utils/sendAnalyticsEvent';
import BbMap from 'common/components/Map/BbMap';
import {
  InfoWindow,
  Marker,
  CircleRadius,
} from 'common/components/Map/BbMap/Objects';
import moment from 'common/deps/moment';
import ActivityInformation from 'components/ActivityInformation';
import NewActivityBlock from 'common/components/NewActivityBlock';
import AboutActivity from 'components/AboutActivity';
import BaseProductReview from 'components/BaseProductReview';
import BreadCrumb from 'components/BreadCrumb';
import MoreInformation from 'components/MoreInformation';
import Rating from 'common/components/Rating';
import BbHorizontalSlider from 'common/components/BbHorizontalSlider';
import BbModal from 'common/components/BbModal';
import HomeCard from 'components/HomeCard';
import Activity from 'assets/graphql/queries/Activity.graphql';
import CartQuery from 'assets/graphql/queries/Cart.graphql';
import RelatedActivities from 'assets/graphql/queries/RelatedActivities.graphql';
import MapImgMobile from 'assets/images/Mapa-Mobile.png';
import MapImgDesktop from 'assets/images/Mapa-Desktop.png';
import PurchaseDetailButton from 'components/PurchaseDetailButton';
import PurchaseDetailBox from 'components/PurchaseDetailBox/PurchaseDetailBox.vue';
import ReservationModal from 'components/ReservationModal';
import GiftMessageModal from 'components/GiftMessageModal';
import ThanksModal from 'components/ThanksModal';
import customCatalogs from 'constants/customCatalogs';
import BoxSlug from 'assets/graphql/queries/BoxSlug.graphql';
import ChangeToCreditBoxDisabledIDs from 'constants/ChangeToCreditBoxDisabledIDs';
import updateHeaderAndFooter from 'utils/updateHeaderAndFooter';
import useAccount from 'composables/useAccount';
import useShoppingCartCleanUp from 'composables/useShoppingCartCleanUp';
import { addToCartService } from 'common/services/utils/addToCart';
import { convertBreadcrumbPath } from 'utils/breadcrumbUrlConverter';

const BASE_COLAPINTO_URL =
  'https://wa.me/+5491121689612?text=Hola%2C+queria+consultar+sobre+la+experiencia+de+Franco+en+';
const COLAPINTO = [
  ['525831', `${BASE_COLAPINTO_URL}Brasil`],
  ['525952', `${BASE_COLAPINTO_URL}Brasil%20en%20la%20Heineken%20Vila'`],
  ['526678', `${BASE_COLAPINTO_URL}Baku`],
  ['526679', `${BASE_COLAPINTO_URL}Singapur`],
  ['526680', `${BASE_COLAPINTO_URL}Texas`],
  ['526681', `${BASE_COLAPINTO_URL}Mexico`],
  ['526682', `${BASE_COLAPINTO_URL}Brasil`],
  ['526683', `${BASE_COLAPINTO_URL}Las+Vegas`],
  ['526684', `${BASE_COLAPINTO_URL}Qatar`],
  ['526685', `${BASE_COLAPINTO_URL}Abu+Dabi`],
];

@Component({
  apollo: {
    cart: {
      query: CartQuery,
    },
    activity: {
      query: Activity,
      fetchPolicy: 'network-only',
      variables() {
        return {
          slug: this.$route.params.activitySlug,
        };
      },
      update({ activity }) {
        const { breadcrumb, catalog_case, is_catalog, gift_information } =
          activity.activity_view_config;
        const originalBreadcrumb = JSON.parse(breadcrumb);
        // Convert legacy URLs to modern format
        this.breadcrumb = {
          ...originalBreadcrumb,
          path: convertBreadcrumbPath(originalBreadcrumb.path),
        };
        this.giftInformation = JSON.parse(gift_information);
        this.catalogCase = JSON.parse(catalog_case);
        this.isCatalog = is_catalog;
        this.expirationDate = moment(activity.dynamic_expiration_date).format(
          'DD/MM/YYYY',
        );
        this.isReserved = ['reserved', 'validated_and_not_reserved'].includes(
          this.giftInformation.status,
        );
        this.changeToCreditAllowed = !this.isReserved;
        activity.locations =
          activity.partneractivity?.locations ||
          activity.comboactivity.locations;

        this.isComboActivity = this.activity?.comboactivity?.is_combo_activity;

        if (
          this.isComboActivity &&
          this.giftInformation?.status === 'validated' &&
          this.activity?.comboactivity?.main_activities?.length ===
            this.giftInformation?.count_validation_activities
        ) {
          this.$router.push({ name: 'accountGifts' });
        }

        this.isReservationBuy =
          activity.partneractivity?.can_purchase_a_reservation &&
          activity.partneractivity?.can_purchase_a_reservation !== 'INACTIVE' &&
          !activity.activity_view_config.is_catalog;

        if (this.isReservationBuy) {
          this.catalogModalComponent = {
            name: 'CatalogReservationConfigModal',
            props: {
              isReservationBuy: true,
              locations: activity.locations,
              activity: activity,
            },
          };
        }
        // isCatalog means you have a hash in URL
        if (this.isCatalog) {
          this.isSpecialReservation =
            activity.partneractivity?.is_special_reservation;
          /* catalogCase is the modal to display when clicking on "Elegir esta experiencia"
          For more info, check custom_catalog project */
          if (!this.catalogCase) {
            this.catalogCodeComponent = null;
            this.catalogModalComponent = null;
          } else {
            this.catalogCodeComponent = {
              name: this.catalogCase.code_component,
              props: {
                code: this.catalogCase.code,
                activity: activity,
                categoryColor: this.breadcrumb.color,
                reservedGift: this.giftInformation,
                isReserved: this.isReserved,
              },
            };
            this.catalogModalComponent = {
              name: this.catalogCase.modal_component,
              props: {
                code: this.catalogCase.code,
                codeId: this.catalogCase.code_id,
                categoryColor: this.breadcrumb.color,
                locations: activity.locations,
                activity: activity,
                isReserved: this.isReserved,
                reservedGift: this.giftInformation,
                codeExpirationDate: this.giftInformation.expiration_date,
                isComboActivity: this.isComboActivity,
              },
            };
          }
        }
        this.activityIsLoading = false;
        this.sendAnalytics(activity);
        return activity;
      },
      result(data) {
        if (data.error) this.$router.push({ name: '404' });
      },
    },
    relatedActivities: {
      query: RelatedActivities,
      variables() {
        return {
          amount: 9,
        };
      },
      update(data) {
        return data.related_activities;
      },
    },
    box: {
      query: BoxSlug,
      skip() {
        return !this.$route.params.boxSlug;
      },
      variables() {
        return {
          box_slug: this.$route.params.boxSlug,
        };
      },
      update(data) {
        this.changeToCreditAllowed =
          !ChangeToCreditBoxDisabledIDs.includes(data.box.id) &&
          !this.isReserved;
        this.isBoxFriendDay = data.box.is_friends_day;
        this.availableBox = data.box.available;
        this.boxName = data.box.name;
        // update header and footer display
        updateHeaderAndFooter(
          data.box.show_main_header,
          data.box.show_main_footer,
        );
        return data.box;
      },
    },
  },
  components: {
    AboutActivity,
    NewActivityBlock,
    ActivityInformation,
    BaseProductReview,
    BreadCrumb,
    PurchaseDetailButton,
    PurchaseDetailBox,
    BbMap,
    MoreInformation,
    Rating,
    BbHorizontalSlider,
    BbModal,
    ReservationModal,
    GiftMessageModal,
    HomeCard,
    ThanksModal,
    ...customCatalogs,
  },

  setup() {
    const { getUserData, getUserIsBigboxPlus } = useAccount();
    const { shoppingCartCleanUp } = useShoppingCartCleanUp();
    return {
      getUserData,
      shoppingCartCleanUp,
      getUserIsBigboxPlus,
    };
  },
})
export default class BaseActivityViewComponent extends Vue {
  isCatalog = '';
  catalogCase = '';
  catalogCodeComponent = null;
  catalogModalComponent = null;
  giftInformation = '';
  mapClicked = false;
  showSticky = false;
  lastScrollPosition = 0;
  loading = false;
  showError = false;
  MapImgMobile = MapImgMobile;
  MapImgDesktop = MapImgDesktop;
  maxMobileResolution = 768;
  windowWidth = null;
  showCatalogModal = false;
  expirationDate = '';
  breadcrumb = null;
  openedModal = true;
  showThanksModal = false;
  reservedGift = '';
  isReserved = false;
  isSpecialReservation = false;
  friendsDayActivities = ['203068', '202780', '202904'];
  isMicrosite = this.$route.params.hash === '~';
  changeToCreditAllowed = true;
  activityIsLoading = true;
  canCloseCatalogModal = true;
  isBoxFriendDay = false;
  availableBox = true;
  boxName = null;
  sentToAnalytics = false;
  isReservationBuy = false;
  isComboActivity = false;

  beforeMount() {
    this.recalculateWidth();
  }

  get isForSale() {
    return (
      !this.isCatalog && this.activity?.activity_view_config?.individual_sale
    );
  }

  get email() {
    return this.getUserData?.email || '';
  }

  get MapImg() {
    if (this.windowWidth <= this.maxMobileResolution) {
      this.MapImage = this.MapImgMobile;
    } else {
      this.MapImage = this.MapImgDesktop;
    }
    return this.MapImage;
  }

  mounted() {
    window.addEventListener('scroll', this.onScroll);
    window.addEventListener('resize', this.recalculateWidth);
  }

  sendAnalytics(activity) {
    // IDK the query run twice
    if (!this.sentToAnalytics) {
      sendItemToAnalytics('view_item', [activity], undefined, {
        userData: {
          email: this.email,
        },
      });
      this.sentToAnalytics = true;
    }
    return;
  }

  get deliveryForAllCountry() {
    return this.activity.locations.some(location => {
      return location.delivery_in_all_country;
    });
  }

  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.recalculateWidth);
  }

  recalculateWidth() {
    this.windowWidth = window.innerWidth;
  }

  get showMap() {
    return this.activity.activity_type !== 'online';
  }

  get mapText() {
    const even = element => element.delivery_radius !== null;
    if (this.isDelivery && this.activity.locations.some(even)) {
      return 'Ver rango de Delivery';
    }
    return 'Ver en el mapa';
  }

  get isCartFull() {
    if (this.cart) {
      return this.cart.items.length >= this.cart_limit;
    }
    return false;
  }

  get isDelivery() {
    return this.activity.activity_type === 'delivery';
  }

  get deliveryText() {
    if (
      this.isDelivery &&
      !this.deliveryForAllCountry &&
      this.hasDeliveryFree
    ) {
      return 'Rango del delivery';
    }
    return 'Mapa y Contacto';
  }

  get hasDeliveryFree() {
    return this.activity.locations.some(location => {
      return location.delivery_radius !== null;
    });
  }

  handlePurchaseButtonClick() {
    if (this.activityIsConsultation) {
      this.redirectToWhatsapp();
    } else if (COLAPINTO.find(c => c[0] === this.activity?.id?.toString())) {
      // TODO: Cuando no esten mas las actividades de colapinto eliminar esto
      this.redirectToWhatsappColapinto();
    } else {
      this.addToCart();
    }
  }

  async addToCart(reservationData = null) {
    this.loading = !this.loading;

    // Extraer activitysQuantity del resto de datos de reservación
    const { activitysQuantity, ...pendingReservationData } =
      reservationData || {};

    const data = {
      boxId: '',
      activityId: this.activity.id.toString(),
      quantity: activitysQuantity || 1,
      isGroup: false,
      source: '',
      boxType: 'digital',
      sellingSpotId: '',
      kindId: '',
      campaignSlug: this.$route.query.campaignSlug,
      landingSlug: this.$route.query.landingSlug,
    };

    // Solo agregar pending_reservation si hay datos de reserva (no solo activitysQuantity)
    if (reservationData && Object.keys(pendingReservationData).length > 0) {
      data.pending_reservation = pendingReservationData;
    }

    await this.shoppingCartCleanUp(this.cart, data);
    const response = await addToCartService(data, this.$apollo);

    if (response.ok) {
      sendItemToAnalytics('add_to_cart', [this.activity], undefined, {
        transaction_id: response.order?.id ?? '',
        userData: {
          email: this.email,
        },
      });
      window.location = `https://${
        window.location.host + response.redirect_url
      }`;
    } else {
      this.loading = !this.loading;
    }
  }

  /**
   Create an array of activity objects for send props to BbMap, in case the activity have more than 1 activity
   * its going to create an object per location because BbMap needed. Yes its a negrada.
   * @returns {[]}
   */
  get parsedActivityLocations() {
    let locationsArray = [];
    if (this.$apollo.queries.activity.loading === false) {
      this.infoWindow = new InfoWindow({
        component: 'BbGoogleMapDefaultInfoWindow',
      });
      locationsArray = this.activity.locations.map(location => {
        const marker = new Marker({
          id: location.id,
          position: { lat: location.latitude, lng: location.longitude },
          infoWindowContext: this.activity,
        });
        if (
          this.activity.activity_type === 'delivery' &&
          location.delivery_radius
        ) {
          marker.circleRadius = new CircleRadius({
            radius: location.delivery_radius,
            center: { lat: location.latitude, lng: location.longitude },
          });
        }
        marker.settings.customMarker = false;
        return marker;
      });
    }
    return locationsArray;
  }

  /* Sticky appears when user scrolls down */
  onScroll() {
    const currentScrollPosition = window.pageYOffset;
    if (currentScrollPosition < 0) {
      return;
    }
    this.showSticky = currentScrollPosition > 940;
  }

  toggleCatalogModal() {
    if (this.canCloseCatalogModal) {
      this.$emit('showOverlay');
      this.showCatalogModal = !this.showCatalogModal;
    }
  }

  toggleThanksModal() {
    this.showThanksModal = !this.showThanksModal;
  }

  openThanksModal() {
    this.showThanksModal = true;
  }

  closeMessageModal() {
    sessionStorage.setItem(`openedModal-${this.giftInformation.id}`, 'true');
    this.openedModal = false;
  }

  get isMobile() {
    return this.windowWidth < this.maxMobileResolution;
  }

  get modalMaxWidth() {
    if (
      this.catalogCase?.modal_component === 'CatalogSpecialReservationCodeModal'
    )
      return '615px';
    if (this.catalogCase?.modal_component === 'CatalogCinemaCodeModal')
      return '385px';
    return this.catalogCase?.catalog_case === 'BoxCodeCatalogReservationConfig'
      ? '515px'
      : '485px';
  }

  goToActivityDetail(activity) {
    const currentParams = this.$router.currentRoute.params;
    currentParams.activitySlug = activity.slug;
    this.$router.push({ params: currentParams });
    this.$apollo.queries.relatedActivities.refetch();
  }

  hasBuyerMessage() {
    return (
      this.giftInformation.buyer_message &&
      !Object.values(this.giftInformation.buyer_message).includes('')
    );
  }

  reservationCreation(status) {
    this.canCloseCatalogModal = !status.loading;
    if (status.success) {
      this.toggleCatalogModal();
      this.$apollo.queries.activity.refetch();
    }
  }

  redirectToWhatsapp() {
    if (!this.activity?.consultation_link) return;
    const wppLink = this.activity?.consultation_link;
    window.open(wppLink, '_blank', 'noopener');
  }

  // TODO: Cuando no esten mas las actividades de colapinto eliminar esto
  redirectToWhatsappColapinto() {
    const cola = COLAPINTO.find(c => c[0] === this.activity?.id?.toString());
    window.open(cola[1], '_blank', 'noopener');
  }

  get itemMessage() {
    return this.hasBuyerMessage() ? this.giftInformation.buyer_message : null;
  }

  get isGiftActivity() {
    return this.activity.__typename === 'ActivityType';
  }

  get showModal() {
    const openedModalSession = Boolean(
      sessionStorage.getItem(`openedModal-${this.giftInformation.id}`),
    );
    return (
      this.isCatalog &&
      this.isGiftActivity &&
      this.itemMessage &&
      this.$route.name !== 'box-activity-catalog' &&
      !openedModalSession
    );
  }

  get buyerInfo() {
    return {
      name: this.giftInformation.buyer_name,
      email: this.giftInformation.buyer_email,
    };
  }

  get isBuyerCorpo() {
    return this.giftInformation.is_buyer_corpo;
  }

  get hideThanksBtn() {
    return (
      this.giftInformation.is_selfgift ||
      this.giftInformation.buyer_email === null
    );
  }

  get activityIsBigboxPlus() {
    return (this.activity.product_tags || []).some(
      tag => tag?.slug?.toLowerCase() === 'bigboxplus',
    );
  }

  get activityIsConsultation() {
    return this.activity.is_consultation;
  }

  get currencySymbol() {
    return this.activity.currency_symbol;
  }

  get activityPriceFrom() {
    return this.activity.price_from;
  }

  get isRetailMode() {
    return this.$route.query?.retail === 'true';
  }

  destroyed() {
    updateHeaderAndFooter();
  }
}
